import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChallengeActivityQuery } from '@examdojo/challenge';
import { McqAttemptResponseHttpModel, McqAttemptResponseQuery, McqAttemptResponseService } from '@examdojo/question';
import { RealtimeChangesListenEvent, RealtimeService } from '@examdojo/supabase';
import { filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { ExamdojoPlatformService } from '../../../shared/platform.service';

@Injectable()
export class ChallengeActivityMcqResponseRealtimeService {
  constructor(
    private readonly realtimeService: RealtimeService,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly mcqResponseService: McqAttemptResponseService,
    private readonly mcqResponseQuery: McqAttemptResponseQuery,
    private readonly platformService: ExamdojoPlatformService,
  ) {
    this.listenForMcqResponseChangesInChallengeActivity().pipe(takeUntilDestroyed()).subscribe();
  }

  private listenForMcqResponseChangesInChallengeActivity() {
    return this.challengeActivityQuery.activeChallengeActivityId$.pipe(
      switchMap((challengeActivityId) => {
        if (!challengeActivityId) {
          return of(null);
        }

        return this.realtimeService
          .listenBroadcastRealtime<McqAttemptResponseHttpModel>(
            [{ event: RealtimeChangesListenEvent.INSERT }, { event: RealtimeChangesListenEvent.UPDATE }],
            `challenge_activity_mcq_response:${challengeActivityId}`,
          )
          .pipe(
            withLatestFrom(this.platformService.activeStatusState$),
            filter(([_, isActive]) => isActive),
            map(([event]) => event),
            tap(({ payload }) => {
              const record = payload?.record;
              if (!record?.question_attempt_id || record.question_attempt_grading_id) {
                return;
              }

              const existingResponse = this.mcqResponseQuery.getByQuestionAttemptId(record.question_attempt_id);

              if (existingResponse && new Date(existingResponse.updated_at) >= new Date(record.updated_at)) {
                return;
              }

              this.mcqResponseService.upsertToLocalStore(record.question_attempt_id, record);
            }),
          );
      }),
    );
  }
}
